import { createContext, useContext, useEffect } from 'react';
import { tabs, type PaneId } from '../state/tabs.ts';

// Toetsen: sneltoetsen gelden alleen als de focus niet in een tekstveld staat (behalve Escape),
// niet zolang een venster of vel (role=dialog) open staat, en Enter op een knop of link is een
// klik, geen sneltoets. Met twee panelen in beeld gelden de sneltoetsen van een scherm alleen in
// het actieve paneel (`activePane` in de tabstaat: de laatste klik, aanraking of focus in een
// paneel, zie de schil); de schil zet daarvoor `PaneContext` om elk paneel. Een sneltoets buiten
// een paneel (v voor de spraakmodus, in de schil zelf) geldt altijd, en met één paneel in beeld
// gelden alle sneltoetsen zoals altijd. Een venster sluit Esc zelf, vóór elk paneel (vangfase op document).

export function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || Boolean(el.isContentEditable);
}

/** Staat er een venster of vel open? Dan zijn de sneltoetsen van het scherm eronder uit (Escape niet: die sluit). */
export function dialogOpen(): boolean {
  return typeof document !== 'undefined' && document.querySelector('[role="dialog"]') !== null;
}

/** Staat de focus op iets dat Enter zelf afhandelt (knop, link, menu-item)? */
export function activates(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el && typeof el.closest === 'function' && el.closest('button, a[href], [role="button"], [role="menuitem"], summary') !== null;
}

/** Het paneel waarin een scherm staat als er twee panelen in beeld zijn; anders null (één paneel, de telefoon, de schil zelf). */
export const PaneContext = createContext<PaneId | null>(null);

/** Gelden de sneltoetsen van dit paneel nu? Buiten een paneel altijd; in een paneel alleen als het het actieve is. */
export function paneActive(pane: PaneId | null): boolean {
  return pane === null || tabs.get().activePane === pane;
}

export type KeyHandler = (e: KeyboardEvent) => boolean | void;

/** Registreert een sneltoetsafhandelaar; geef true terug om de toets te consumeren. In een paneel alleen als dat het actieve is. */
export function useShortcuts(handler: KeyHandler, deps: unknown[]): void {
  const pane = useContext(PaneContext);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if (!paneActive(pane)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTyping(e.target) && e.key !== 'Escape') return;
      if (e.key !== 'Escape' && dialogOpen()) return;
      if (e.key === 'Enter' && activates(e.target)) return;
      if (handler(e)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, pane]);
}
